import "../JSONTools/JSONformatter.css";
import ToolHeader from "../../Components/ToolHeader";
import "./TextUtilities.css";
import { Row, Col } from "react-bootstrap";
import { useEffect, useMemo, useRef, useState } from "react";
import FormatterPanel from "../../HomePage/FormatterPanel";
import { InputHeaderButtons } from "../JSONTools/JSONToolsHeaderButtons";
import { FindReplaceAPI } from "../../Services/APIService";
import { ReactComponent as CopyIcon } from "../../Icons/Copy.svg";
const sampleText = `Hello World! Contact us at hello@example.com or
support@example.com.
Visit https://example.com for more info.
Phone: +1-555-0123, +1-555-0199
Order #1024 shipped on 2025-05-12.`;

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function buildFlags(options) {
    return [
        options.global ? "g" : "",
        options.caseInsensitive ? "i" : "",
        options.multiline ? "m" : "",
        options.dotAll ? "s" : ""
    ].join("");
}

function buildPattern(findText, options) {
    if (!findText) {
        return null;
    }

    const source = options.regex ? findText : escapeRegExp(findText);
    const boundedSource = options.wholeWord ? `\\b(?:${source})\\b` : source;

    try {
        return new RegExp(boundedSource, buildFlags(options));
    } catch {
        return null;
    }
}

function buildGlobalPattern(pattern) {
    if (!pattern) {
        return null;
    }

    const flags = pattern.flags.includes("g") ? pattern.flags : `${pattern.flags}g`;
    return new RegExp(pattern.source, flags);
}

function Toggle({ label, checked, onChange }) {
    return (
        <label className="find-toggle">
            <input
                type="checkbox"
                checked={checked}
                onChange={(event) => onChange(event.target.checked)}
            />
            <span className="find-toggle-track" />
            <span className="find-toggle-label">{label}</span>
        </label>
    );
}

function FindReplace() {
    const [sourceText, setSourceText] = useState(sampleText);
    const [findText, setFindText] = useState("(\\w+)@(\\w+\\.\\w+)");
    const [replaceText, setReplaceText] = useState("[email:$1 at $2]");
    const [apiOutputText, setApiOutputText] = useState(sampleText);
    const [apiMatchCount, setApiMatchCount] = useState(0);
    const [apiError, setApiError] = useState("");
    const [activeOutputTab, setActiveOutputTab] = useState("result");
    const findReplaceRequestId = useRef(0);
    const [hasCopied, setHasCopied] = useState(false);
    const toolFav = { toolName: 'Find & Replace', Iconpath: "/FileCodeRaw.svg" };

    const [options, setOptions] = useState({
        regex: true,
        caseInsensitive: false,
        global: true,
        multiline: false,
        dotAll: false,
        wholeWord: false
    });

    const pattern = useMemo(() => buildPattern(findText, options), [findText, options]);

    const matches = useMemo(() => {
        if (!pattern) {
            return [];
        }

        const countPattern = buildGlobalPattern(pattern);
        return Array.from(sourceText.matchAll(countPattern));
    }, [pattern, sourceText]);

    const matchCount = apiMatchCount;

    const outputText = useMemo(() => {
        return apiOutputText;
    }, [apiOutputText]);

       

    

    useEffect(() => {
        const requestId = findReplaceRequestId.current + 1;
        findReplaceRequestId.current = requestId;

        if (!findText) {
            setApiOutputText(sourceText);
            setApiMatchCount(0);
            setApiError("");
            return;
        }

        const timeoutId = setTimeout(async () => {
            try {
                const response = await FindReplaceAPI({
                    input: sourceText,
                    pattern: findText,
                    replacement: replaceText
                });

                if (requestId === findReplaceRequestId.current) {
                    setApiOutputText(response.data.outpute ?? response.data.output ?? "");
                    setApiMatchCount(response.data.matchCount ?? 0);
                    setApiError("");
                }
            } catch (error) {
                if (requestId === findReplaceRequestId.current) {
                    setApiError(error.response?.data?.message || error.message);
                }
            }
        }, 250);

        return () => clearTimeout(timeoutId);
    }, [findText, replaceText, sourceText]);

    const setOption = (key, value) => {
        setOptions((current) => ({
            ...current,
            [key]: value
        }));
    };

    const flagsText = buildFlags(options) || "-";

    const matchesText = useMemo(() => (
        matches.map((match, index) => {
            const captures = match
                .slice(1)
                .map((group, groupIndex) => `$${groupIndex + 1} = ${group ?? ""}`)
                .join("\n");

            return [
                `#${index + 1} · index ${match.index} Len ${match[0].length}`,
                match[0],
                captures
            ].filter(Boolean).join("\n");
        }).join("\n\n")
    ), [matches]);

    const copyActiveOutput = async () => {
        const value = activeOutputTab === "matches"
            ? matchesText
            : activeOutputTab === "preview"
                ? sourceText
                : outputText;

        await navigator.clipboard.writeText(value);
        setHasCopied(true);
        setTimeout(() => setHasCopied(false), 2000);
    };

    const renderPreview = () => {
        if (!matches.length) {
            return sourceText;
        }

        const chunks = [];
        let cursor = 0;

        matches.forEach((match, index) => {
            const start = match.index;
            const end = start + match[0].length;

            if (start > cursor) {
                chunks.push(sourceText.slice(cursor, start));
            }

            chunks.push(
                <mark key={`${start}-${end}-${index}`} className="find-preview-mark">
                    {sourceText.slice(start, end)}
                </mark>
            );

            cursor = end;
        });

        if (cursor < sourceText.length) {
            chunks.push(sourceText.slice(cursor));
        }

        return chunks;
    };

    const customOutputContent = (
        <div className="find-output-content">
            <div className="find-output-tabs">
                {[
                    { key: "result", label: "Result" },
                    { key: "preview", label: "Preview" },
                    { key: "matches", label: "Matches" }
                ].map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        className={`find-output-tab ${activeOutputTab === tab.key ? "active" : ""}`}
                        onClick={() => setActiveOutputTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}

                <button
                    type="button"
                    className="find-output-copy"
                    onClick={copyActiveOutput}
                >
                    <CopyIcon className="headerIcon" />
                    {hasCopied ? "Copied!" : "Copy"}
                </button>
            </div>

            <div className="find-output-body">
                {activeOutputTab === "result" && (
                    <pre className="find-output-pre">{outputText}</pre>
                )}

                {activeOutputTab === "preview" && (
                    <pre className="find-output-pre">{renderPreview()}</pre>
                )}

                {activeOutputTab === "matches" && (
                    <pre className="find-output-pre find-matches-pre">
                        {matchesText || "No matches"}
                    </pre>
                )}
            </div>
        </div>
    );

    const customInputContent = (
        <div className="find-custom-content">
            <textarea
                value={sourceText}
                onChange={(event) => setSourceText(event.target.value)}
                placeholder="Paste source text here..."
                className="find-source-textarea"
            />

            <div className="find-fields-row">
                <label className="find-field">
                    <span>Find (regex)</span>
                    <input
                        value={findText}
                        onChange={(event) => setFindText(event.target.value)}
                        placeholder="Find text or pattern"
                    />
                </label>

                <label className="find-field">
                    <span>Replace with (use $1, $2...)</span>
                    <input
                        value={replaceText}
                        onChange={(event) => setReplaceText(event.target.value)}
                        placeholder="Replacement"
                    />
                </label>
            </div>

            <div className="find-toggle-grid">
                <Toggle label="Regex" checked={options.regex} onChange={(value) => setOption("regex", value)} />
                <Toggle label="Case-insensitive (i)" checked={options.caseInsensitive} onChange={(value) => setOption("caseInsensitive", value)} />
                <Toggle label="Global (g)" checked={options.global} onChange={(value) => setOption("global", value)} />
                <Toggle label="Multiline (m)" checked={options.multiline} onChange={(value) => setOption("multiline", value)} />
                <Toggle label="Dot-all (s)" checked={options.dotAll} onChange={(value) => setOption("dotAll", value)} />
                <Toggle label="Whole word" checked={options.wholeWord} onChange={(value) => setOption("wholeWord", value)} />
            </div>
        </div>
    );

    return (
        <Col>
            <ToolHeader title="Find & Replace" description="Search text with regex options and preview replacements" toolFav={toolFav} />

            <Row className='Formatter find-replace-layout'>
                <Col xs={12} lg={6} className="find-replace-column">
                    <FormatterPanel
                        title="Source Text"
                        value={sourceText}
                        customContent={customInputContent}
                        panelClass="find-panel"
                        panelStyle={{ height: "386px" }}
                        headerButtons={
                            <InputHeaderButtons
                                onImport={(fileContent) => setSourceText(fileContent)}
                                onClear={() => setSourceText("")}
                                accept=".txt,.json,.csv,.md,.log"
                            />
                        }
                        footerLeft={`Flags: /${flagsText}/`}
                        footerRight={`${matchCount} ${matchCount === 1 ? "match" : "matches"}`}
                    />
                </Col>

                <Col xs={12} lg={6} className="find-replace-column">
                    <FormatterPanel
                        title="Output"
                        value={outputText}
                        customContent={customOutputContent}
                        panelClass="find-output-panel"
                        panelStyle={{ height: "386px" }}
                        readOnly
                        footerLeft={`Characters : ${outputText.replaceAll(" ", "").length}`}
                        footerRight={apiError ? `Error : ${apiError}` : `Size : ${new Blob([outputText]).size} B`}
                    />
                </Col>
            </Row>
        </Col>
    );
}

export default FindReplace;
